<template>

	<!-- Main Container -->
	<div class="main-container">
		<!-- Hero Section -->
		<section class="hero-section">
			<h1 class="hero-title">知识管理</h1>
			<p class="hero-subtitle">管理专业知识库，支持文档导入、实体提取和关系构建</p>
		</section>

		<!-- Knowledge Library Status -->
		<!-- <div class="management-section">
			<div class="section-header">
				<h2 class="section-title">专业知识库状态</h2>
			</div>
			<div class="stats-grid">
				<div class="stat-card" v-for="library in knowledgeLibraries" :key="library.name">
					<div class="stat-icon" :style="{ background: library.color }">{{ library.icon }}</div>
					<div class="stat-number">{{ library.size }}</div>
					<div class="stat-label">{{ library.name }}</div>
				</div>
			</div>
		</div> -->

		<!-- Document Import -->
		<div class="management-section">
			<div class="section-header">
				<h2 class="section-title">文档资料导入</h2>
				<div class="graph-selector">
					图谱:<el-select v-model="currentGraph" placeholder="请选择图谱" class="graph-select" @change="switchGraph">
						<el-option v-for="item in knowledgeGraphs" :key="item.id" :label="item.gh_name_zh"
							:value="item.gh_code">
						</el-option>
					</el-select>
				</div>
				<div class="section-actions">
					<button class="btn btn-secondary" @click="batchImport">批量导入</button>
					<button class="btn btn-primary" @click="showFeatureDialog"
						:disabled="selectedFiles.length === 0 || isProcessing">
						{{ isProcessing ? '处理中...' : '开始处理' }}
					</button>
				</div>
			</div>

			<div class="upload-area" :class="{ 'drag-over': isDragOver }" @click="triggerFileInput"
				@dragover.prevent="handleDragOver" @dragleave.prevent="handleDragLeave" @drop.prevent="handleDrop">
				<div class="upload-icon">📁</div>
				<div class="upload-text">拖拽文件到此处或点击上传</div>
				<!-- <div class="upload-hint">支持 PDF、Word、Excel 等格式，单次最多上传 100MB</div> -->
				<button class="btn btn-primary">选择文件</button>
				<input type="file" ref="fileInput" style="display: none;" @change="handleFileSelect">
			</div>

			<!-- Selected Files List -->
			<div v-if="selectedFiles.length > 0" class="file-list">
				<div class="file-item" v-for="(file, index) in selectedFiles" :key="index">
					<div class="file-icon">{{ getFileIcon(file.name) }}</div>
					<div class="file-info">
						<div class="file-name">{{ file.name }}</div>
						<div class="file-size">{{ formatFileSize(file.size) }}</div>
					</div>
					<div class="file-actions">
						<button class="btn btn-secondary" @click="removeFile(index)">移除</button>
					</div>
				</div>
			</div>

			<!-- Processing Progress -->
			<div v-if="showProgress" class="progress-list">
				<div class="progress-item" v-for="step in processingSteps" :key="step.id">
					<div class="progress-icon" :class="step.status">{{ step.icon }}</div>
					<div class="progress-info">
						<div class="progress-title">{{ step.title }}</div>
						<div class="progress-desc">{{ step.description }}</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Processing Results -->
		<div v-if="processingResults.length > 0" class="management-section">
			<div class="section-header">
				<h2 class="section-title">处理结果</h2>
				<div class="section-actions">
					<button class="btn btn-secondary" @click="exportResults">导出结果</button>
					<button class="btn btn-primary" @click="saveToKnowledgeBase">保存到知识库</button>
				</div>
			</div>

			<div class="stats-grid">
				<div class="stat-card">
					<div class="stat-icon" style="background: var(--primary);">🔍</div>
					<div class="stat-number">{{ processingResults.reduce((sum, r) => sum + r.entities, 0) }}</div>
					<div class="stat-label">识别实体</div>
				</div>
				<div class="stat-card">
					<div class="stat-icon" style="background: var(--secondary);">🔗</div>
					<div class="stat-number">{{ processingResults.reduce((sum, r) => sum + r.relations, 0) }}</div>
					<div class="stat-label">提取关系</div>
				</div>
				<div class="stat-card">
					<div class="stat-icon" style="background: var(--warning);">📊</div>
					<div class="stat-number">{{ processingResults.length }}</div>
					<div class="stat-label">处理文档</div>
				</div>
			</div>
		</div>

		<!-- 特征填写对话框 -->
		<el-dialog title="提取特征" :visible.sync="featureDialogVisible" width="600px" :close-on-click-modal="false"
			:close-on-press-escape="false">
			<div class="feature-dialog-content">
				<div class="feature-description">
					<p>请根据需求填写要提取的特征类型，例如：人物、地点、时间、技术要点、材料属性等</p>
				</div>
				<div class="feature-input">
					<label class="feature-label">特征类型：</label>
					<el-input v-model="featureInput" type="textarea" :rows="4"
						placeholder="请输入要提取的特征类型，多个特征用逗号分隔，如：人物,地点,时间,技术要点" class="feature-textarea"></el-input>
				</div>
				<div class="feature-examples">
					<h4>示例：</h4>
					<div class="example-tags">
						<span class="example-tag" @click="addExample('人物')">人物</span>
						<span class="example-tag" @click="addExample('地点')">地点</span>
						<span class="example-tag" @click="addExample('时间')">时间</span>
						<span class="example-tag" @click="addExample('技术要点')">技术要点</span>
						<span class="example-tag" @click="addExample('材料属性')">材料属性</span>
						<span class="example-tag" @click="addExample('工艺流程')">工艺流程</span>
						<span class="example-tag" @click="addExample('质量标准')">质量标准</span>
						<span class="example-tag" @click="addExample('安全要求')">安全要求</span>
					</div>
				</div>
			</div>
			<div slot="footer" class="dialog-footer">
				<el-button @click="cancelFeatureDialog">取消</el-button>
				<el-button type="primary" @click="confirmFeatureDialog" :disabled="!featureInput.trim()">
					确定
				</el-button>
			</div>
		</el-dialog>
	</div>

</template>

<script>
	import {
		mapGetters
	} from 'vuex'
	export default {
		name: 'CourseManagementPage',
		components: {},
		data() {
			return {
				isDragOver: false,
				selectedFiles: [],
				isProcessing: false,
				showProgress: false,
				processingResults: [],
				currentGraph: '',
				knowledgeGraphs: [],
				featureDialogVisible: false,
				featureInput: '',
				knowledgeLibraries: [{
						name: '建筑工程技术知识库',
						size: '2.1GB',
						color: 'var(--primary)',
						icon: '🏗️'
					},
					{
						name: '钢结构应用知识库',
						size: '1.8GB',
						color: 'var(--secondary)',
						icon: '🔧'
					},
					{
						name: '教材标准库',
						size: '1.3GB',
						color: 'var(--accent)',
						icon: '📖'
					}
				],
				processingSteps: [{
						id: 1,
						title: '文档解析',
						description: '自动提取文本内容，格式标准化处理',
						icon: '📄',
						status: 'pending'
					},
					{
						id: 2,
						title: '实体识别',
						description: 'AI识别专业术语、概念、技术要点',
						icon: '🔍',
						status: 'pending'
					},
					{
						id: 3,
						title: '关系抽取',
						description: '分析实体间的关联关系',
						icon: '🔗',
						status: 'pending'
					},
					{
						id: 4,
						title: '图谱构建',
						description: '生成知识图谱并优化布局',
						icon: '🎯',
						status: 'pending'
					}
				]
			}
		},
		mounted() {
			this.loadKnowledgeGraphs();
		},
		computed: {
			...mapGetters([
				'currentUser'
			]),
			totalKnowledgeSize() {
				// Calculate total size from all libraries
				const sizes = this.knowledgeLibraries.map(lib => parseFloat(lib.size));
				const total = sizes.reduce((sum, size) => sum + size, 0);
				return `${total.toFixed(1)}GB`;
			}
		},
		methods: {
			triggerFileInput() {
				this.$refs.fileInput.click();
			},

			handleDragOver() {
				this.isDragOver = true;
			},

			handleDragLeave() {
				this.isDragOver = false;
			},

			handleDrop(event) {
				this.isDragOver = false;
				const files = Array.from(event.dataTransfer.files);
				this.addFiles(files);
			},

			handleFileSelect(event) {
				const files = Array.from(event.target.files);
				this.addFiles(files);
				// Clear input value to allow selecting same file again
				event.target.value = '';
			},

			addFiles(files) {
				const validFiles = files
				// files.filter(file => {
				// 	const validTypes = ['.jsonl','.pdf', '.doc', '.docx', '.xls', '.xlsx'];
				// 	const extension = '.' + file.name.split('.').pop().toLowerCase();
				// 	return validTypes.includes(extension);
				// });
				this.selectedFiles.push(...validFiles);

				// if (validFiles.length < files.length) {
				// 	alert(`已过滤 ${files.length - validFiles.length} 个不支持的文件格式`);
				// }
			},

			removeFile(index) {
				this.selectedFiles.splice(index, 1);
			},

			getFileIcon(filename) {
				const extension = filename.split('.').pop().toLowerCase();
				const icons = {
					'pdf': '📄',
					'doc': '📝',
					'docx': '📝',
					'xls': '📊',
					'xlsx': '📊'
				};
				return icons[extension] || '📄';
			},

			formatFileSize(bytes) {
				if (bytes === 0) return '0 Bytes';
				const k = 1024;
				const sizes = ['Bytes', 'KB', 'MB', 'GB'];
				const i = Math.floor(Math.log(bytes) / Math.log(k));
				return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
			},

			// 显示特征填写对话框
			showFeatureDialog() {
				if (this.selectedFiles.length === 0) {
					alert('请先选择要处理的文件');
					return;
				}
				if (!this.currentGraph) {
					alert('请先选择图谱');
					return;
				}
				this.featureDialogVisible = true;
				this.featureInput = '';
			},

			// 添加示例特征
			addExample(feature) {
				if (this.featureInput) {
					if (!this.featureInput.includes(feature)) {
						this.featureInput += ',' + feature;
					}
				} else {
					this.featureInput = feature;
				}
			},

			// 取消特征对话框
			cancelFeatureDialog() {
				this.featureDialogVisible = false;
				this.featureInput = '';
			},

			// 确认特征对话框
			confirmFeatureDialog() {
				if (!this.featureInput.trim()) {
					alert('请输入特征类型');
					return;
				}

				this.featureDialogVisible = false;
				this.startProcessing();
			},

			// 开始处理文件
			startProcessing() {
				this.isProcessing = true;
				this.showProgress = true;

				// Reset all steps to pending
				this.processingSteps.forEach(step => {
					step.status = 'pending';
				});

				// 调用自动特征提取接口
				this.callAutoFeatureAPI().then(response => {
					console.log('接口调用成功，开始处理动画', response);
					if (response.data.data) {
						// Start processing animation
						this.processNextStep(0);
					} else {
						this.$message.error(response.data.message);
						this.isProcessing = false;
						this.showProgress = false;
					}
				}).catch(error => {
					console.error('处理失败:', error);
					this.$message.error('处理失败，请重试');
					this.isProcessing = false;
					this.showProgress = false;
				});
			},

			// 调用自动特征提取接口
			callAutoFeatureAPI() {
				const formData = new FormData();

				// 添加文件
				if (this.selectedFiles.length > 0) {
					formData.append('file', this.selectedFiles[0]); // 先处理第一个文件
				}

				// 添加特征
				formData.append('features', this.featureInput.trim());

				// 添加图谱代码
				formData.append('gh_code', this.currentGraph);

				console.log('准备调用接口，参数:', {
					file: this.selectedFiles[0]?.name,
					features: this.featureInput.trim(),
					gh_code: this.currentGraph
				});

				// 先上传文件到 file_info 表
				return this.uploadFileToFileInfo(this.selectedFiles[0]).then(() => {
					// 文件上传成功后，调用特征提取接口
					return this.HttpUtils.post('/api/tool/autoFeature', formData, {
						headers: {
							'Content-Type': 'multipart/form-data'
						}
					}).then(response => {
						console.log('特征提取成功:', response);
						return response;
					}).catch(error => {
						console.error('特征提取失败:', error);
						throw error;
					});
				}).catch(error => {
					console.error('文件上传失败:', error);
					throw error;
				});
			},
			// 上传文件到 file_info 表
			uploadFileToFileInfo(file) {
				const formData = new FormData();
				formData.append('file', file);
				
				// 获取当前用户ID（如果有）
				const currentUser = this.currentUser;
				if (currentUser && currentUser.user_id) {
					formData.append('upload_user_id', currentUser.user_id.toString());
				}
				
				// 添加描述
				formData.append('description', `图谱: ${this.currentGraph} - ${file.name}`);
				
				// 添加图谱代码
				formData.append('gh_code', this.currentGraph);

				return this.HttpUtils.post('/api/file-info/upload', formData, {
					headers: {
						'Content-Type': 'multipart/form-data'
					}
				}).then(response => {
					console.log('文件已保存到 file_info 表:', response);
					this.$message.success('文件上传成功');
					return response;
				}).catch(error => {
					console.error('保存文件信息失败:', error);
					this.$message.warning('文件上传成功，但保存文件信息失败');
					// 即使保存失败，也继续处理
					return Promise.resolve();
				});
			},

			processNextStep(stepIndex) {
				if (stepIndex >= this.processingSteps.length) {
					// Processing completed
					this.isProcessing = false;
					this.generateResults();
					this.$message({
						message: '知识图谱构建完成！',
						type: 'success'
					});
					return;
				}

				const step = this.processingSteps[stepIndex];
				step.status = 'processing';

				// Simulate processing time
				setTimeout(() => {
					step.status = 'completed';
					this.processNextStep(stepIndex + 1);
				}, 2000 + Math.random() * 1000);
			},

			generateResults() {
				// Generate mock processing results
				this.processingResults = this.selectedFiles.map(file => ({
					filename: file.name,
					entities: Math.floor(Math.random() * 50) + 20,
					relations: Math.floor(Math.random() * 80) + 30,
					concepts: Math.floor(Math.random() * 30) + 15
				}));
			},

			batchImport() {
				alert('批量导入功能开发中...');
			},

			exportResults() {
				alert('导出处理结果...');
			},

			saveToKnowledgeBase() {
				alert('保存到知识库成功！');
				// Clear results after saving
				this.processingResults = [];
				this.selectedFiles = [];
				this.showProgress = false;

				// Reset processing steps
				this.processingSteps.forEach(step => {
					step.status = 'pending';
				});
			},

			// 加载知识图谱列表
			loadKnowledgeGraphs() {
				this.HttpUtils.get('/api/gh-rel/', {}).then(result => {
					this.knowledgeGraphs = result.data;
					if (this.knowledgeGraphs && this.knowledgeGraphs.length > 0) {
						this.currentGraph = this.knowledgeGraphs[0].gh_code;
					}
				}).catch(error => {
					console.error('加载图谱列表失败:', error);
					// 如果API调用失败，使用模拟数据
					this.knowledgeGraphs = [{
							id: 1,
							gh_code: 'jzgc',
							gh_name_zh: '建筑工程技术',
							authors: '张三,李四',
							institution: '某大学建筑工程学院'
						},
						{
							id: 2,
							gh_code: 'gjg',
							gh_name_zh: '钢结构应用',
							authors: '王五,赵六',
							institution: '某大学土木工程学院'
						}
					];
					if (this.knowledgeGraphs.length > 0) {
						this.currentGraph = this.knowledgeGraphs[0].gh_code;
					}
				});
			},

			// 切换图谱
			switchGraph(graphCode) {
				this.currentGraph = graphCode;
				console.log('切换到图谱:', graphCode);
				// 这里可以添加切换图谱后的逻辑
			}

		}
	}
</script>

<style scoped>
	/* Hero Section */
	.hero-section {
		text-align: center;
		margin-bottom: 60px;
	}

	.hero-title {
		font-size: clamp(32px, 5vw, 48px);
		font-weight: 800;
		line-height: 1.1;
		margin-bottom: 16px;
		background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
	}

	.hero-subtitle {
		font-size: 18px;
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
		line-height: 1.6;
	}

	/* Statistics Cards */
	.stats-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
		gap: 20px;
		margin-bottom: 40px;
	}

	.stat-card {
		background: white;
		border-radius: var(--radius-xl);
		padding: 24px;
		box-shadow: var(--shadow-md);
		border: 1px solid var(--border-light);
		transition: var(--transition-base);
	}

	.stat-card:hover {
		transform: translateY(-4px);
		box-shadow: var(--shadow-xl);
	}

	.stat-icon {
		width: 50px;
		height: 50px;
		border-radius: var(--radius-md);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 24px;
		color: white;
		margin-bottom: 16px;
	}

	.stat-number {
		font-size: 32px;
		font-weight: 800;
		color: var(--text-primary);
		margin-bottom: 4px;
	}

	.stat-label {
		font-size: 14px;
		color: var(--text-secondary);
		font-weight: 500;
	}

	/* Management Section */
	.management-section {
		background: white;
		border-radius: var(--radius-xl);
		padding: 32px;
		box-shadow: var(--shadow-md);
		border: 1px solid var(--border-light);
		margin-bottom: 40px;
	}

	.section-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 24px;
		flex-wrap: wrap;
		gap: 16px;
	}

	.section-title {
		font-size: 20px;
		font-weight: 700;
		color: var(--text-primary);
	}

	.section-actions {
		display: flex;
		gap: 12px;
	}

	/* Graph Selector */
	.graph-selector {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.graph-select {
		width: 350px;
		/* padding: 12px 16px; */
		/* border: 2px solid #D1D5DB;
		border-radius: 12px; */
		background: white;
		font-size: 16px;
		font-weight: 600;
		color: #111827;
		cursor: pointer;
		transition: all 0.3s ease;
	}

	/deep/.graph-select .el-input__inner {
		border-radius: 12px;
		padding: 12px 16px;
		border: 2px solid #E5E7EB;
		height: 47px;
	}

	.graph-select:hover {
		border-color: var(--primary);
	}

	.graph-select:focus {
		outline: none;
		border-color: var(--primary);
		box-shadow: 0 0 0 2px rgba(46, 92, 230, 0.1);
	}

	/* Upload Area */
	.upload-area {
		border: 2px dashed var(--border-medium);
		border-radius: var(--radius-lg);
		padding: 40px;
		text-align: center;
		background: var(--bg-gray-50);
		transition: var(--transition-base);
		cursor: pointer;
		margin-bottom: 20px;
	}

	.upload-area:hover {
		border-color: var(--primary);
		background: rgba(46, 92, 230, 0.02);
	}

	.upload-area.drag-over {
		border-color: var(--primary);
		background: rgba(46, 92, 230, 0.05);
	}

	.upload-icon {
		font-size: 48px;
		color: var(--text-tertiary);
		margin-bottom: 16px;
	}

	.upload-text {
		font-size: 16px;
		font-weight: 600;
		color: var(--text-primary);
		margin-bottom: 8px;
	}

	.upload-hint {
		font-size: 12px;
		color: var(--text-secondary);
		margin-bottom: 16px;
	}

	/* Buttons */
	.btn {
		padding: 8px 16px;
		border: none;
		border-radius: var(--radius-md);
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		transition: var(--transition-base);
	}

	.btn-primary {
		background: var(--primary);
		color: white;
	}

	.btn-primary:hover {
		background: var(--primary-dark);
	}

	.btn-secondary {
		background: var(--bg-gray-100);
		color: var(--text-primary);
		border: 1px solid var(--border-medium);
	}

	.btn-secondary:hover {
		background: var(--bg-gray-200);
	}

	.btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	/* Progress Indicators */
	.progress-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.progress-item {
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 16px;
		background: var(--bg-gray-50);
		border-radius: var(--radius-md);
		border: 1px solid var(--border-light);
	}

	.progress-icon {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 18px;
		color: white;
		transition: var(--transition-base);
	}

	.progress-icon.pending {
		background: var(--text-tertiary);
	}

	.progress-icon.processing {
		background: var(--warning);
		animation: pulse 1.5s infinite;
	}

	.progress-icon.completed {
		background: var(--success);
	}

	@keyframes pulse {

		0%,
		100% {
			opacity: 1;
		}

		50% {
			opacity: 0.5;
		}
	}

	.progress-info {
		flex: 1;
	}

	.progress-title {
		font-size: 14px;
		font-weight: 600;
		color: var(--text-primary);
		margin-bottom: 4px;
	}

	.progress-desc {
		font-size: 12px;
		color: var(--text-secondary);
	}

	/* File List */
	.file-list {
		margin-top: 20px;
	}

	.file-item {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px;
		background: white;
		border-radius: var(--radius-md);
		border: 1px solid var(--border-light);
		margin-bottom: 8px;
	}

	.file-icon {
		width: 32px;
		height: 32px;
		background: var(--primary);
		border-radius: var(--radius-sm);
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		font-size: 14px;
	}

	.file-info {
		flex: 1;
	}

	.file-name {
		font-size: 14px;
		font-weight: 600;
		color: var(--text-primary);
		margin-bottom: 2px;
	}

	.file-size {
		font-size: 12px;
		color: var(--text-secondary);
	}

	.file-actions {
		display: flex;
		gap: 8px;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.main-container {
			padding: 20px 10px;
		}

		.hero-title {
			font-size: 28px;
		}

		.stats-grid {
			grid-template-columns: 1fr;
		}

		.section-header {
			flex-direction: column;
			align-items: stretch;
		}
	}

	/* Feature Dialog Styles */
	.feature-dialog-content {
		padding: 20px 0;
	}

	.feature-description {
		margin-bottom: 20px;
		padding: 15px;
		background: #f8f9fa;
		border-radius: var(--radius-md);
		border-left: 4px solid var(--primary);
	}

	.feature-description p {
		margin: 0;
		color: var(--text-secondary);
		font-size: 14px;
		line-height: 1.5;
	}

	.feature-input {
		margin-bottom: 20px;
	}

	.feature-label {
		display: block;
		margin-bottom: 8px;
		font-weight: 600;
		color: var(--text-primary);
		font-size: 14px;
	}

	.feature-textarea {
		width: 100%;
	}

	.feature-examples h4 {
		margin: 0 0 12px 0;
		font-size: 14px;
		font-weight: 600;
		color: var(--text-primary);
	}

	.example-tags {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.example-tag {
		padding: 6px 12px;
		background: #e3f2fd;
		color: var(--primary);
		border: 1px solid #bbdefb;
		border-radius: var(--radius-sm);
		font-size: 12px;
		cursor: pointer;
		transition: var(--transition-base);
	}

	.example-tag:hover {
		background: var(--primary);
		color: white;
		border-color: var(--primary);
	}

	.dialog-footer {
		text-align: right;
		padding-top: 20px;
		border-top: 1px solid var(--border-light);
	}
</style>